import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { SrIcon } from "@/components/ui/SrIcon";
import { formatNOK, formatDateShort } from "@/lib/format";
import { useFeatureFlag } from "@/hooks/use-feature-flag";
import { useProjectPermission } from "@/hooks/use-project-permission";
import { ProjectChip } from "./ProjectChip";
import { HastBadge } from "./HastBadge";
import { InboxAvatar } from "./InboxAvatar";
import type { InboxVoucher } from "@/hooks/use-pm-inbox";

interface Props {
  voucher: InboxVoucher;
  /** VIB-1226: åpner den selvforsynte godkjenningsmodalen for dette bilaget. */
  onOpenApproval: (voucher: InboxVoucher) => void;
}

export function RowBilag({ voucher: v, onOpenApproval }: Props) {
  // VIB-1226: modal-flyten direkte i innboksen bak flagg. Av: dagens
  // deep-link til BilagTab (uendret). cleanupRef VIB-1226 fjerner
  // deep-link-grenen når rollout har stått på "all" en periode.
  const pmInboxApprovalFlag = useFeatureFlag("feature_pm_inbox_approval");
  // Samme permission-gate som BilagTab (VIB-722c) — PM-innboksen viste
  // tidligere Godkjenn uten sjekk fordi innboksen er implisitt PL-scopet;
  // den nye direkte-modal-flyten trenger en eksplisitt sjekk siden den ikke
  // lenger passerer BilagTabs egen gate.
  const canApprove = useProjectPermission(v.projectId, "approve_vouchers");
  const showApprovalButton = !pmInboxApprovalFlag || canApprove;

  return (
    <>
      {/* VIB-959 (fase 2): desktop-grid kun fra xl:, mobilkort under. Ansatt-
          flyt A-funn — PM-innboksen hadde ingen mobiltilpasning. */}
      <div className="hidden xl:grid grid-cols-[24px_1fr_90px_110px_auto_auto] items-center gap-3.5 px-5 py-3.5 border-t border-border/40">
        <InboxAvatar fullName={v.supplierName ?? "Leverandør"} size={24} />
        <div className="min-w-0">
          <div className="flex items-center gap-2.5 mb-1">
            <ProjectChip
              projectId={v.projectId}
              projectCode={v.projectCode}
              projectName={v.projectName}
              customerName={v.customerName}
            />
            <HastBadge ageDays={v.ageDays} />
          </div>
          <div className="text-sm font-medium truncate">
            {v.supplierName ?? "Ukjent leverandør"}
            {v.description ? ` — ${v.description}` : ""}
          </div>
          <div className="text-xs text-muted-foreground">
            Bilag #{v.voucherNumber || "—"}
          </div>
        </div>
        <span className="text-[11px] text-muted-foreground tabular-nums inline-flex items-center gap-1">
          <SrIcon name="calendar" className="w-3 h-3" />
          {formatDateShort(v.dueDate ?? v.voucherDate)}
        </span>
        <span className="text-sm font-semibold tabular-nums whitespace-nowrap text-right">
          {formatNOK(v.amount)}
        </span>
        <Button asChild variant="outline" size="sm" className="h-8 text-xs">
          <Link to={`/prosjekter/${v.projectId}/okonomi?seksjon=bilag&voucherId=${v.id}`}>
            Vis bilag
          </Link>
        </Button>
        <div>
          {showApprovalButton && (
            pmInboxApprovalFlag ? (
              <Button size="sm" className="h-8 text-xs" onClick={() => onOpenApproval(v)}>
                <SrIcon name="check" className="w-3 h-3 mr-1" />
                Godkjenn
              </Button>
            ) : (
              <Button asChild size="sm" className="h-8 text-xs">
                <Link
                  to={`/prosjekter/${v.projectId}/okonomi?seksjon=bilag&voucherId=${v.id}&attest=${v.id}`}
                >
                  <SrIcon name="check" className="w-3 h-3 mr-1" />
                  Godkjenn
                </Link>
              </Button>
            )
          )}
        </div>
      </div>

      <div className="xl:hidden px-4 py-3 border-t border-border/40 space-y-2.5">
        <div className="flex items-start gap-2.5">
          <InboxAvatar fullName={v.supplierName ?? "Leverandør"} size={28} />
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap mb-1">
              <ProjectChip
                projectId={v.projectId}
                projectCode={v.projectCode}
                projectName={v.projectName}
                customerName={v.customerName}
              />
              <HastBadge ageDays={v.ageDays} />
            </div>
            <div className="text-sm font-medium truncate">
              {v.supplierName ?? "Ukjent leverandør"}
              {v.description ? ` — ${v.description}` : ""}
            </div>
            <div className="text-xs text-muted-foreground flex items-center gap-2 flex-wrap">
              <span>Bilag #{v.voucherNumber || "—"}</span>
              <span className="inline-flex items-center gap-1">
                <SrIcon name="calendar" className="w-3 h-3" />
                {formatDateShort(v.dueDate ?? v.voucherDate)}
              </span>
            </div>
          </div>
          <span className="text-sm font-semibold tabular-nums whitespace-nowrap shrink-0">
            {formatNOK(v.amount)}
          </span>
        </div>
        <div className="flex gap-2">
          <Button asChild variant="outline" size="sm" className="flex-1 h-10 text-xs">
            <Link to={`/prosjekter/${v.projectId}/okonomi?seksjon=bilag&voucherId=${v.id}`}>
              Vis bilag
            </Link>
          </Button>
          {showApprovalButton && (
            pmInboxApprovalFlag ? (
              <Button size="sm" className="flex-1 h-10 text-xs" onClick={() => onOpenApproval(v)}>
                <SrIcon name="check" className="w-3.5 h-3.5 mr-1" />
                Godkjenn
              </Button>
            ) : (
              <Button asChild size="sm" className="flex-1 h-10 text-xs">
                <Link
                  to={`/prosjekter/${v.projectId}/okonomi?seksjon=bilag&voucherId=${v.id}&attest=${v.id}`}
                >
                  <SrIcon name="check" className="w-3.5 h-3.5 mr-1" />
                  Godkjenn
                </Link>
              </Button>
            )
          )}
        </div>
      </div>
    </>
  );
}
